import { useEffect, useState } from "react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Field = ({ field, value, onChange }) => {
  const common = { "data-testid": `edit-${field.name}-input`, className: "mt-1 h-11 border-slate-300" };
  if (field.type === "select") {
    return (
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}
              data-testid={`edit-${field.name}-input`}
              className="mt-1 h-11 w-full border border-slate-300 bg-white px-3 text-sm">
        {field.allowEmpty && <option value="">—</option>}
        {(field.options || []).map((o) =>
          typeof o === "string"
            ? <option key={o} value={o}>{o}</option>
            : <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.type === "textarea") {
    return (
      <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)}
                data-testid={`edit-${field.name}-input`}
                className="mt-1 min-h-[70px] border-slate-300" />
    );
  }
  return (
    <Input type={field.type || "text"} value={value ?? ""} step={field.type === "number" ? "any" : undefined}
           onChange={(e) => onChange(e.target.value)} {...common} />
  );
};

// Generic "correct this record" dialog. fields: [{name,label,type,options,span,hint}]
export const EditDialog = ({ open, onOpenChange, title, description, fields, values, onSave, testid = "edit-record-dialog", submitLabel = "Save corrections" }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      const seed = {};
      fields.forEach((f) => {
        const raw = values?.[f.name];
        seed[f.name] = f.type === "date" && typeof raw === "string" ? raw.slice(0, 10) : (raw ?? "");
      });
      setForm(seed);
    }
  }, [open, fields, values]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {};
      fields.forEach((f) => {
        const v = form[f.name];
        if (v === "" || v === null || v === undefined) return;
        body[f.name] = f.type === "number" ? Number(v) : v;
      });
      await onSave(body);
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description || "Fix a typo or wrong entry. Every correction is written to the audit trail."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            {fields.map((f) => (
              <div key={f.name} className={f.span === 2 ? "sm:col-span-2" : ""}>
                <Label className="text-xs uppercase tracking-wide text-slate-600">{f.label}</Label>
                <Field field={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
                {f.hint && <p className="mt-1 text-[11px] text-slate-500">{f.hint}</p>}
              </div>
            ))}
          </div>
          <Button type="submit" disabled={busy} data-testid="save-corrections-button"
                  className="h-11 w-full bg-[#134e4a] hover:bg-[#0f3f3c]">
            {busy ? "Saving…" : submitLabel}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Reason-captured soft retire / void. Nothing is ever hard deleted.
export const RetireDialog = ({ open, onOpenChange, title, description, placeholder, confirmLabel = "Retire record", onConfirm, testid = "retire-record-dialog" }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  const go = async () => {
    if (reason.trim().length < 5) return toast.error("Give a reason of at least 5 characters");
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={testid}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description || "It disappears from the working list but stays on file under the Retired filter, with the full audit trail."}
          </DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                  data-testid="retire-reason-textarea" className="min-h-[90px] border-slate-300"
                  placeholder={placeholder || "Reason — e.g. entered in error, duplicate of the record above"} />
        <Button onClick={go} disabled={busy} data-testid="confirm-retire-record-button"
                className="h-11 bg-red-800 hover:bg-red-900">
          {busy ? "Saving…" : confirmLabel}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
